import React from 'react';
import {useTranslation} from 'react-i18next';

import findLastIndex from 'lodash/findLastIndex';

import {Switch} from '@edna/components';
import {Divider, Icon} from '@edna/components/primitives';

import authTemplateLocalization, {
  EMessageAlign,
} from 'src/containers/MessageMatchers/WhatsAppForm/AuthTemplateLocalization';
import {ETemplateButtonOtpType} from 'src/containers/MessageMatchers/WhatsAppForm/definitions';
import {
  EMessageMatcherButtonTypes,
  TMessageMatcher,
  TMessageMatcherButton,
} from 'src/containers/MessageMatchers/definitions';
import {deleteVariable} from 'src/containers/MessageMatchers/utils';
import {CopyIcon, ExternalLinkIcon, ListIcon, PhoneIcon, ReplyIcon} from 'src/icons';

import * as SS from '../style';
import {FOOTER_TIME} from '../utils';
import {Header} from './Header';
import * as S from './style';

type TProps = {
  content: TMessageMatcher['content'];
  language?: string;
  noActions?: boolean;
  children: React.ReactNode;
};

const MAX_SHORT_VIEW_BUTTONS = 3;
const BUTTON_ICON: Partial<Record<EMessageMatcherButtonTypes, TSvgComponentType>> = {
  [EMessageMatcherButtonTypes.CHAT]: ReplyIcon,
  [EMessageMatcherButtonTypes.PHONE]: PhoneIcon,
  [EMessageMatcherButtonTypes.URL]: ExternalLinkIcon,
  [EMessageMatcherButtonTypes.OTP]: CopyIcon,
};

const hasIcon = (button: TMessageMatcherButton) => {
  if (button.buttonType === EMessageMatcherButtonTypes.OTP) {
    return button.otpType === ETemplateButtonOtpType.COPY;
  }

  return button.buttonType !== EMessageMatcherButtonTypes.CHAT;
};

const Content = React.memo<
  TProps & {canExpandButtons?: boolean; buttons: TProps['content']['buttons']}
>(({content, buttons = [], language, children, canExpandButtons}) => {
  const {t, i18n} = useTranslation();

  const localizationIndex = authTemplateLocalization.findIndex(
    (item) => item.code.toLowerCase() === (language?.toLowerCase() || i18n.language),
  );
  const align = authTemplateLocalization[localizationIndex]?.align;
  const hasButtons = !!buttons.length;

  return (
    <SS.MessageWrapper>
      <S.Message isRtlDirection={align === EMessageAlign.RTL} hasButtons={hasButtons}>
        {content.header && <Header header={content.header} />}
        <S.Text>
          {children}
          {!!content.codeExpirationMinutes && localizationIndex >= 0 && (
            <>
              <br />
              <S.Footer>
                {t(`WhatsAppAuthTemplate:${localizationIndex}.passwordExpirationText`, {
                  count: Number(content.codeExpirationMinutes ?? 0),
                })}
              </S.Footer>
            </>
          )}
          {content.footer?.text && (
            <>
              <br />
              <S.Footer>{content.footer.text}</S.Footer>
            </>
          )}
          <SS.Time>
            <span>{FOOTER_TIME}</span>
          </SS.Time>
        </S.Text>
        {hasButtons && (
          <S.ButtonList>
            {buttons
              .slice(0, canExpandButtons ? MAX_SHORT_VIEW_BUTTONS - 1 : MAX_SHORT_VIEW_BUTTONS)
              .map((button, index) => (
                <S.Button
                  key={index}
                  title={
                    button.buttonType === EMessageMatcherButtonTypes.URL
                      ? deleteVariable(button.url)
                      : ''
                  }
                >
                  {button.buttonType && hasIcon(button) && (
                    <S.ButtonIcon as={BUTTON_ICON[button.buttonType!]} />
                  )}
                  {button.text}
                </S.Button>
              ))}
            {canExpandButtons && (
              <S.Button>
                <S.ButtonIcon as={ListIcon} />
                {t('MessagePreview:allPossibilities')}
              </S.Button>
            )}
          </S.ButtonList>
        )}
      </S.Message>
    </SS.MessageWrapper>
  );
});

Content.displayName = 'Content';

const AllButtons = React.memo<{buttons: TProps['content']['buttons']}>(({buttons = []}) => {
  const {t} = useTranslation();

  const divisionIndex = React.useMemo(() => {
    if (!buttons.length) {
      return 0;
    }

    const lastIndex = findLastIndex(buttons, (button) =>
      buttons[0].buttonType === EMessageMatcherButtonTypes.CHAT
        ? button.buttonType === EMessageMatcherButtonTypes.CHAT
        : button.buttonType !== EMessageMatcherButtonTypes.CHAT,
    );

    return lastIndex === -1 ? buttons.length : lastIndex + 1;
  }, [buttons]);

  const firstGroup = buttons.slice(0, divisionIndex);
  const secondGroup = buttons.slice(divisionIndex);

  return (
    <S.AllButtonsWrapper>
      <S.AllButtons>
        <S.Title>
          <S.CloseIcon />
          {t('MessagePreview:allButtons')}
        </S.Title>
        <S.ButtonList>
          {firstGroup.map((button, index) => (
            <S.ListButton key={index}>
              {button.buttonType && (
                <Icon as={BUTTON_ICON[button.buttonType!]} size="16px" color="grey500" />
              )}
              {button.text}
            </S.ListButton>
          ))}
          {!!firstGroup.length && !!secondGroup.length && <Divider />}
          {secondGroup.map((button, index) => (
            <S.ListButton key={index}>
              {button.buttonType && (
                <Icon as={BUTTON_ICON[button.buttonType!]} size="16px" color="grey500" />
              )}
              {button.text}
            </S.ListButton>
          ))}
        </S.ButtonList>
      </S.AllButtons>
    </S.AllButtonsWrapper>
  );
});

AllButtons.displayName = 'AllButtons';

export const WhatsAppContent = React.memo<TProps>(
  ({content = {}, language, noActions, children}) => {
    const {t} = useTranslation();
    const [isExpandedButtons, setExpandButtons] = React.useState<boolean>(false);

    const handleChangeMessagesView = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        setExpandButtons(event.target.checked);
      },
      [],
    );

    const buttons = content.buttons?.filter((button) => button.text) ?? [];

    const canExpandButtons =
      !noActions &&
      buttons.filter((button) => button.buttonType !== EMessageMatcherButtonTypes.OTP).length >
        MAX_SHORT_VIEW_BUTTONS;

    return (
      <>
        {canExpandButtons && (
          <Switch
            label={t('MessagePreview:expandButtons')}
            checked={isExpandedButtons}
            onChange={handleChangeMessagesView}
          />
        )}
        {isExpandedButtons && <AllButtons buttons={buttons} />}
        {!isExpandedButtons && (
          <Content
            content={content}
            language={language}
            canExpandButtons={canExpandButtons}
            buttons={buttons}
          >
            {children}
          </Content>
        )}
      </>
    );
  },
);

WhatsAppContent.displayName = 'WhatsAppContent';
